import React, { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Alert, AlertDescription } from "../components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Plus,
  Search,
  Filter,
  MoreHorizontal,
  Edit,
  Trash2,
  Settings,
  TrendingUp,
  Users,
  Loader2,
  AlertCircle,
  CheckCircle,
  X,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { PlanTier } from "../types/subscription";
import {
  AdminService,
  PlanTierFilters,
  PaginationParams,
} from "../services/adminService";
import { PlanTierForm } from "../components/admin/PlanTierForm";
import { PlanTierViewer } from "../components/admin/PlanTierViewer";
import { AdminSidebar, AdminTab } from "../components/admin/AdminSidebar";
import { SystemConfig } from "../components/admin/SystemConfig";
import LossFactorsManagement from "../components/admin/LossFactorsManagement";
import NetworkTariffsManagement from "../components/admin/NetworkTariffsManagement";
import TouRatesManagement from "../components/admin/TouRatesManagement";
import { ChargeMapManagement } from "../components/admin/ChargeMapManagement";
import { NtaThresholdsManagement } from "../components/admin/NtaThresholdsManagement";
import HolidaysManagement from "../components/admin/HolidaysManagement";
import { ExportPage } from "../../../../features/billValidation/pages/ExportPage";
import { StateImagesTab } from "../../../../features/scopingAi/components/admin/StateImagesTab";
import { useToast } from "../hooks/use-toast";
import { ADMIN_CONFIG, ADMIN_TOAST_MESSAGES } from "../utils/constants";

const AdminPortal: React.FC = () => {
  // Tab management
  const [activeTab, setActiveTab] = useState<AdminTab>("loss-factors");

  // Subscription management states
  const [planTiers, setPlanTiers] = useState<PlanTier[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
  });

  // Form states
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingPlan, setEditingPlan] = useState<PlanTier | null>(null);
  const [viewingPlan, setViewingPlan] = useState<PlanTier | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // Filter and pagination states
  const [filters, setFilters] = useState<PlanTierFilters>({});
  const [pagination, setPagination] = useState<PaginationParams>({
    page: 1,
    limit: ADMIN_CONFIG.PLAN_TIERS.DEFAULT_PAGE_SIZE,
  });
  const [searchTerm, setSearchTerm] = useState("");

  const { toast } = useToast();

  useEffect(() => {
    // if (activeTab === "subscriptions") {
    //   loadPlanTiers();
    //   loadStats();
    // }
  }, [filters, pagination, activeTab]);

  const loadPlanTiers = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await AdminService.getPlanTiers(filters, pagination);

      if (result.error) {
        setError(result.error);
      } else {
        setPlanTiers(result.data);
        setTotalCount(result.count);
      }
    } catch (error) {
      setError("Failed to load plan tiers");
    } finally {
      setIsLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const result = await AdminService.getPlanTierStats();
      if (result.data) {
        setStats(result.data);
      }
    } catch (error) {
      console.error("Failed to load stats:", error);
    }
  };

  const handleSearch = () => {
    setFilters((prev) => ({
      ...prev,
      search: searchTerm,
    }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleFilterChange = (key: keyof PlanTierFilters, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handlePlanSaved = (planTier: PlanTier) => {
    setShowCreateForm(false);
    setEditingPlan(null);
    loadPlanTiers();
    loadStats();

    toast({
      title: "Success",
      description: editingPlan
        ? ADMIN_TOAST_MESSAGES.PLAN_UPDATED
        : ADMIN_TOAST_MESSAGES.PLAN_CREATED,
    });
  };

  const handleDeletePlan = async (planTier: PlanTier) => {
    if (
      !confirm(
        `Are you sure you want to delete "${planTier.name}"? This will set it as inactive.`
      )
    ) {
      return;
    }

    try {
      const result = await AdminService.deletePlanTier(planTier.id);

      if (result.error) {
        toast({
          title: "Error",
          description: result.error,
          variant: "destructive",
        });
      } else {
        loadPlanTiers();
        loadStats();
        toast({
          title: "Success",
          description: ADMIN_TOAST_MESSAGES.PLAN_DELETED,
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: ADMIN_TOAST_MESSAGES.PLAN_DELETE_ERROR,
        variant: "destructive",
      });
    }
  };

  const toggleRowExpansion = (planId: string) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(planId)) {
        newSet.delete(planId);
      } else {
        newSet.add(planId);
      }
      return newSet;
    });
  };

  const formatFeatures = (features: Record<string, any>) => {
    const featureElements: JSX.Element[] = [];

    if (features.scopingai) {
      const scopingAiEnabled = features.scopingai.scopingAi !== false;
      featureElements.push(
        <div key="scopingai" className="flex items-center gap-2">
          {scopingAiEnabled ? (
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
          ) : (
            <X className="w-3 h-3 text-red-500 stroke-[3]" />
          )}
          <span className="text-sm text-gray-700">ScopingAi</span>
        </div>
      );
    }

    if (features.knowledgebase) {
      const knowledgebaseEnabled = features.knowledgebase.enabled !== false;
      featureElements.push(
        <div key="knowledgebase" className="flex items-center gap-2">
          {knowledgebaseEnabled ? (
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
          ) : (
            <X className="w-3 h-3 text-red-500 stroke-[3]" />
          )}
          <span className="text-sm text-gray-700">Knowledge Base</span>
        </div>
      );
    }

    return featureElements.length > 0 ? (
      <div className="flex flex-col gap-1.5">{featureElements}</div>
    ) : (
      <span className="text-sm text-gray-400">No features</span>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const totalPages = Math.ceil(
    totalCount / (pagination.limit || ADMIN_CONFIG.PLAN_TIERS.DEFAULT_PAGE_SIZE)
  );

  const renderSubscriptionManagement = () => (
    <div className="space-y-6">
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Settings className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">Total Plans</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <div>
                <p className="text-sm text-muted-foreground">Active</p>
                <p className="text-2xl font-bold text-green-600">
                  {stats.active}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-red-500" />
              <div>
                <p className="text-sm text-muted-foreground">Inactive</p>
                <p className="text-2xl font-bold text-red-600">
                  {stats.inactive}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>


      </div>

      {/* Filters and Actions */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1 flex gap-2">
              <Input
                placeholder="Search plan tiers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleSearch()}
              />
              <Button onClick={handleSearch} variant="outline">
                <Search className="h-4 w-4" />
              </Button>
            </div>

            <div className="flex gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline">
                    <Filter className="mr-2 h-4 w-4" />
                    Filter
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem
                    onClick={() => handleFilterChange("is_active", true)}
                  >
                    Active Plans
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => handleFilterChange("is_active", false)}
                  >
                    Inactive Plans
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setFilters({})}>
                    All Plans
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <Button onClick={() => setShowCreateForm(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Create Plan
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Plan Tiers Table */}
      <Card>
        <CardHeader>
          <CardTitle>Plan Tiers ({totalCount})</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
              <span className="ml-2">Loading plan tiers...</span>
            </div>
          ) : planTiers.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No plan tiers found
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Plan</TableHead>
                    <TableHead>Tier</TableHead>
                    <TableHead>Price</TableHead>
                    <TableHead>Features</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {planTiers.map((plan) => (
                    <React.Fragment key={plan.id}>
                      <TableRow
                        className="cursor-pointer hover:bg-muted/50 transition-colors"
                        onClick={() => toggleRowExpansion(plan.id)}
                      >
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {expandedRows.has(plan.id) ? (
                              <ChevronDown className="h-4 w-4 text-gray-500" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-gray-500" />
                            )}
                            <div>
                              <div className="font-medium">{plan.name}</div>
                              <div className="text-sm text-muted-foreground">
                                {plan.description}
                              </div>
                            </div>
                          </div>
                        </TableCell>
                      <TableCell>
                        <Badge variant="outline">{plan.tier_level}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">
                          ${plan.price_monthly}/mo
                        </div>
                        {plan.price_yearly && (
                          <div className="text-sm text-muted-foreground">
                            ${plan.price_yearly}/yr
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="max-w-xs">
                          {formatFeatures(plan.features)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={plan.is_active ? "default" : "secondary"}
                        >
                          {plan.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDate(plan.created_at)}
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent>
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingPlan(plan);
                              }}
                            >
                              <Edit className="mr-2 h-4 w-4" />
                              Edit Plan
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeletePlan(plan);
                              }}
                              className="text-red-600"
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Delete Plan
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>

                    {/* Expanded Row Content */}
                    {expandedRows.has(plan.id) && (
                      <TableRow>
                        <TableCell colSpan={5} className="p-0">
                          <div className="bg-gray-50 border-t border-gray-200 p-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">

                              {/* Basic Information */}
                              <div className="space-y-3">
                                <h4 className="font-semibold text-gray-900 text-sm">Plan Details</h4>
                                <div className="space-y-2">
                                  <div>
                                    <span className="text-xs text-gray-500">Name:</span>
                                    <p className="text-sm font-medium">{plan.name}</p>
                                  </div>
                                  <div>
                                    <span className="text-xs text-gray-500">Description:</span>
                                    <p className="text-sm">{plan.description}</p>
                                  </div>
                                  <div>
                                    <span className="text-xs text-gray-500">Tier Level:</span>
                                    <p className="text-sm">{plan.tier_level}</p>
                                  </div>
                                </div>
                              </div>

                              {/* Features Details */}
                              <div className="space-y-3">
                                <h4 className="font-semibold text-gray-900 text-sm">Features</h4>
                                <div className="space-y-2">
                                  {plan.features.scopingai && (
                                    <div>
                                      <span className="text-xs text-gray-500">ScopingAi:</span>
                                      <div className="flex items-center gap-2 mt-1">
                                        {plan.features.scopingai.scopingAi !== false ? (
                                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                        ) : (
                                          <X className="w-3 h-3 text-red-500 stroke-[3]" />
                                        )}
                                        <span className="text-sm">
                                          {plan.features.scopingai.scopingAi !== false ? 'Enabled' : 'Disabled'}
                                        </span>
                                      </div>
                                      {plan.features.scopingai.proposals_limit && (
                                        <p className="text-xs text-gray-500 mt-1">
                                          Proposals Limit: {plan.features.scopingai.proposals_limit}
                                        </p>
                                      )}
                                    </div>
                                  )}

                                  {plan.features.knowledgebase && (
                                    <div>
                                      <span className="text-xs text-gray-500">Knowledge Base:</span>
                                      <div className="flex items-center gap-2 mt-1">
                                        {plan.features.knowledgebase.enabled !== false ? (
                                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                        ) : (
                                          <X className="w-3 h-3 text-red-500 stroke-[3]" />
                                        )}
                                        <span className="text-sm">
                                          {plan.features.knowledgebase.enabled !== false ? 'Enabled' : 'Disabled'}
                                        </span>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Status & Metadata */}
                              <div className="space-y-3">
                                <h4 className="font-semibold text-gray-900 text-sm">Status</h4>
                                <div className="space-y-2">
                                  <div>
                                    <span className="text-xs text-gray-500">Status:</span>
                                    <div className="mt-1">
                                      <Badge variant={plan.is_active ? "default" : "secondary"}>
                                        {plan.is_active ? "Active" : "Inactive"}
                                      </Badge>
                                    </div>
                                  </div>
                                  <div>
                                    <span className="text-xs text-gray-500">Created:</span>
                                    <p className="text-sm">
                                      {new Date(plan.created_at).toLocaleDateString()}
                                    </p>
                                  </div>
                                  <div>
                                    <span className="text-xs text-gray-500">Updated:</span>
                                    <p className="text-sm">
                                      {new Date(plan.updated_at).toLocaleDateString()}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <div className="text-sm text-muted-foreground">
                    Showing{" "}
                    {((pagination.page || 1) - 1) *
                      (pagination.limit ||
                        ADMIN_CONFIG.PLAN_TIERS.DEFAULT_PAGE_SIZE) +
                      1}{" "}
                    to{" "}
                    {Math.min(
                      (pagination.page || 1) *
                        (pagination.limit ||
                          ADMIN_CONFIG.PLAN_TIERS.DEFAULT_PAGE_SIZE),
                      totalCount
                    )}{" "}
                    of {totalCount} results
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={(pagination.page || 1) <= 1}
                      onClick={() =>
                        setPagination((prev) => ({
                          ...prev,
                          page: (prev.page || 1) - 1,
                        }))
                      }
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={(pagination.page || 1) >= totalPages}
                      onClick={() =>
                        setPagination((prev) => ({
                          ...prev,
                          page: (prev.page || 1) + 1,
                        }))
                      }
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Dialogs */}
      <Dialog open={showCreateForm} onOpenChange={setShowCreateForm}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Plan Tier</DialogTitle>
            <DialogDescription>
              Create a new subscription plan tier with custom features and
              pricing.
            </DialogDescription>
          </DialogHeader>
          <PlanTierForm
            onSave={handlePlanSaved}
            onCancel={() => setShowCreateForm(false)}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!editingPlan}
        onOpenChange={(open) => !open && setEditingPlan(null)}
      >
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Plan Tier</DialogTitle>
            <DialogDescription>
              Update the plan tier details, features, and pricing.
            </DialogDescription>
          </DialogHeader>
          {editingPlan && (
            <PlanTierForm
              planTier={editingPlan}
              onSave={handlePlanSaved}
              onCancel={() => setEditingPlan(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!viewingPlan}
        onOpenChange={(open) => !open && setViewingPlan(null)}
      >
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Plan Tier Details</DialogTitle>
            <DialogDescription>
              View detailed information about this plan tier.
            </DialogDescription>
          </DialogHeader>
          {viewingPlan && <PlanTierViewer planTier={viewingPlan} />}
        </DialogContent>
      </Dialog>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <div className="flex h-screen">
        {/* Sidebar */}
        <AdminSidebar
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />

        {/* Main Content */}
        <div className="flex-1 overflow-auto">
          <div className="p-6">
            {activeTab === "network-tariffs" ? (
              <NetworkTariffsManagement />
            ) : activeTab === "nta-thresholds" ? (
              <NtaThresholdsManagement />
            ) : activeTab === "tou-rates" ? (
              <TouRatesManagement />
            ) : activeTab === "charge-map" ? (
              <ChargeMapManagement />
            ) : activeTab === "holidays" ? (
              <HolidaysManagement />
            ) : activeTab === "config" ? (
              <SystemConfig />
            ) : activeTab === "state-images" ? (
              <StateImagesTab />
            ) : activeTab === "export" ? (
              <ExportPage />
            ) : (
              <LossFactorsManagement />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPortal;
